import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Plan, CompanySystemPlan, System, PlanCheck, PlanValidationResult } from '@/lib/plans/types';
import { parseFeatures } from '@/lib/plans/enforce';
import { toast } from 'sonner';

export type { Plan };

// Hook to fetch all plans for a system (with company count)
export function useSystemPlans(systemCode?: string) {
  return useQuery({
    queryKey: ['system-plans', systemCode],
    queryFn: async () => {
      if (!systemCode) return [];

      const { data: system } = await supabase
        .from('systems')
        .select('id')
        .eq('code', systemCode)
        .single();

      if (!system) throw new Error('System not found');

      // Fetch plans with console sync fields
      const { data: plansData, error: plansError } = await supabase
        .from('plans')
        .select('*, console_plan_id, console_synced_at')
        .eq('system_id', system.id)
        .eq('is_active', true)
        .order('sort_order');

      if (plansError) throw plansError;

      // Fetch company counts for each plan
      const planIds = plansData.map(p => p.id);
      const { data: countsData } = await supabase
        .from('company_system_plans')
        .select('plan_id')
        .in('plan_id', planIds)
        .eq('status', 'active');

      // Count companies per plan
      const countsByPlan: Record<string, number> = {};
      (countsData || []).forEach(row => {
        countsByPlan[row.plan_id] = (countsByPlan[row.plan_id] || 0) + 1;
      });

      return plansData.map(plan => ({
        ...plan,
        features: parseFeatures(plan.features),
        companies_count: countsByPlan[plan.id] || 0
      })) as (Plan & { companies_count: number })[];
    },
    enabled: !!systemCode,
  });
}

// Hook to fetch company's current plans
export function useCompanyPlans(companyId?: string) {
  return useQuery({
    queryKey: ['company-plans', companyId],
    queryFn: async () => {
      if (!companyId) return [];

      // 1️⃣ Query principal com embeds opcionais (LEFT JOIN - sem !inner)
      const { data, error } = await supabase
        .from('company_system_plans')
        .select(`
          *,
          plans:plans (
            id,
            name,
            description,
            price_monthly,
            price_yearly,
            features,
            system_id,
            systems:systems (
              id,
              code,
              name
            )
          ),
          systems:system_id (
            id,
            code,
            name
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'active');

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // 2️⃣ Identificar IDs faltantes para buscar em fallback
      const missingPlanIds = data
        .filter(item => !item.plans)
        .map(item => item.plan_id)
        .filter(Boolean);

      const missingSystemIds = data
        .filter(item => !item.plans?.systems && !item.systems)
        .map(item => item.plans?.system_id || item.system_id)
        .filter(Boolean);

      // 3️⃣ Fetch dados faltantes em paralelo (se necessário)
      let plansFallback: any[] = [];
      let systemsFallback: any[] = [];

      if (missingPlanIds.length > 0) {
        const { data: plansData } = await supabase
          .from('plans')
          .select('id, name, description, price_monthly, price_yearly, features, system_id')
          .in('id', missingPlanIds);
        plansFallback = plansData || [];
      }

      if (missingSystemIds.length > 0) {
        const { data: systemsData } = await supabase
          .from('systems')
          .select('id, code, name')
          .in('id', missingSystemIds);
        systemsFallback = systemsData || [];
      }

      // 4️⃣ Mapear com fallbacks em múltiplos níveis
      const result = data.map(item => {
        // Plan com fallback
        const plan = item.plans || plansFallback.find((p: any) => p.id === item.plan_id);
        
        // System com fallback em 3 níveis de prioridade
        const system = 
          item.plans?.systems || // Nível 1: nested dentro de plans
          item.systems || // Nível 2: join direto em company_system_plans
          systemsFallback.find((s: any) => s.id === (plan?.system_id || item.system_id)); // Nível 3: fallback query

        return {
          ...item,
          plan: plan ? {
            id: plan.id,
            name: plan.name,
            description: plan.description,
            price_monthly: plan.price_monthly,
            price_yearly: plan.price_yearly,
            features: parseFeatures(plan.features)
          } : undefined,
          system: system ? {
            id: system.id,
            code: system.code,
            name: system.name
          } : undefined
        };
      });

      console.log('🔍 [useCompanyPlans] Final result:', result);
      return result;
    },
    enabled: !!companyId,
    staleTime: 30 * 1000, // 30 segundos de cache
  });
}

// Hook to validate plan features/limits
export function usePlanValidation() {
  return useMutation({
    mutationFn: async ({ 
      companyId, 
      systemCode, 
      check 
    }: { 
      companyId: string; 
      systemCode: string; 
      check: PlanCheck 
    }): Promise<PlanValidationResult> => {
      const { data, error } = await supabase.functions.invoke('validate-plan', {
        body: {
          company_id: companyId,
          system_code: systemCode,
          check
        }
      });

      if (error) throw error;
      return data;
    },
  });
}

// Hook to apply plan changes
export function useApplyPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      systemCode,
      planId,
      action,
      reason,
      billingPeriod = 'monthly'
    }: {
      companyId: string;
      systemCode: string;
      planId: string;
      action: 'upgrade' | 'downgrade' | 'cancel' | 'activate' | 'suspend';
      reason?: string;
      billingPeriod?: 'monthly' | 'yearly';
    }) => {
      const { data, error } = await supabase.functions.invoke('apply-plan', {
        body: {
          company_id: companyId,
          system_code: systemCode,
          to_plan_id: planId,
          action,
          reason,
          billing_period: billingPeriod
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['company-plans', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['company-plans-admin', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['plan-audit', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['user-systems'] });
      
      toast.success('Plano alterado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erro ao alterar plano');
    },
  });
}

// Hook to fetch plan audit history
export function usePlanAudit(companyId?: string, systemCode?: string) {
  return useQuery({
    queryKey: ['plan-audit', companyId, systemCode],
    queryFn: async () => {
      if (!companyId) return [];

      let query = supabase
        .from('company_plan_audit')
        .select(`
          *,
          from_plan:plans!company_plan_audit_from_plan_id_fkey (
            id,
            name
          ),
          to_plan:plans!company_plan_audit_to_plan_id_fkey (
            id,
            name
          ),
          systems!inner (
            id,
            code,
            name
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (systemCode) {
        const { data: system } = await supabase
          .from('systems')
          .select('id')
          .eq('code', systemCode)
          .single();

        if (system) {
          query = query.eq('system_id', system.id);
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!companyId,
  });
}

// Hook to fetch all systems (only active ones)
export function useSystems() {
  return useQuery({
    queryKey: ['systems'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('systems')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as System[];
    },
  });
}